import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { APP_LINK_STATE } from '../../Home/navRoutes';
import { COLORS } from '../../IndividualPlan/tokens';
import { gradeHref, gradeName } from '../listKeys';
import type { PeGradeDetail, ScoreUnit } from '../types';
import { EM_DASH, fmtDelta, fmtDeltaScale, fmtLevel, fmtScale } from '../utils/drilldownFormat';
import { CountLink } from './CountLink';
import { SmallGroupMarker } from './SmallGroupMarker';

export const TABLE_SX = {
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${COLORS.divider}`,
  borderRadius: '8px',
  overflowX: 'auto',
  bgcolor: '#FFFFFF',
} as const;

export const ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  px: 2,
  py: 1.5,
  borderTop: `1px solid ${COLORS.divider}`,
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '24px',
  color: COLORS.ink,
  '&:first-of-type': { borderTop: 0 },
} as const;

export const HEAD_ROW_SX = {
  ...ROW_SX,
  bgcolor: COLORS.bg,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: '16px',
  color: COLORS.muted,
} as const;

export const CELL_SX = { width: 150, flexShrink: 0 } as const;
export const FIRST_CELL_SX = {
  flex: '1 1 0',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
} as const;
export const SUB_SX = {
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 500,
  color: COLORS.muted,
  display: 'inline-flex',
  alignItems: 'center',
} as const;

export function scoredSample(stats: { scored: number; scale_scored?: number }, unit: ScoreUnit): number {
  return unit === 'scale_score' ? (stats.scale_scored ?? stats.scored) : stats.scored;
}

function sampleCaption(g: PeGradeDetail, unit: ScoreUnit): string {
  const scored = scoredSample(g, unit);
  return scored < g.count ? `${g.count} students · ${scored} scored` : `based on ${g.count} students`;
}

export function growthCell(
  value: number | null,
  scale: number | null,
  unit: ScoreUnit,
  resetYear: boolean,
  count: number,
): string {
  if (resetYear) return EM_DASH;
  const v = unit === 'scale_score' ? scale : value;
  if (v == null) return count > 0 ? 'First year tested' : EM_DASH;
  return unit === 'scale_score' ? fmtDeltaScale(v) : fmtDelta(v);
}

export function levelCell(level: number | null, scale: number | null, unit: ScoreUnit): string {
  return unit === 'scale_score' ? fmtScale(scale) : fmtLevel(level);
}

export function GradeTable({
  grades,
  unit,
  resetYear,
  threshold,
}: {
  grades: PeGradeDetail[];
  unit: ScoreUnit;
  resetYear: boolean;
  threshold: number;
}) {
  const levelHeader = `${unit === 'scale_score' ? 'Scale score' : 'Composite level'}${resetYear ? ' · new scale' : ''}`;
  return (
    <Box data-testid="grade-table" sx={TABLE_SX}>
      <Box sx={HEAD_ROW_SX}>
        <Box sx={FIRST_CELL_SX}>Grade</Box>
        <Box sx={CELL_SX}>{levelHeader}</Box>
        <Box sx={CELL_SX}>Growth this year</Box>
        <Box sx={{ ...CELL_SX, width: 170 }}>Same grade last year</Box>
        <Box sx={CELL_SX}>Met exit criteria</Box>
      </Box>
      {grades.map((g) => (
        <Box key={g.key} data-testid="grade-row" sx={ROW_SX}>
          <Box sx={FIRST_CELL_SX}>
            <Link
              component={RouterLink}
              to={gradeHref(g.grade)}
              state={APP_LINK_STATE}
              underline="hover"
              sx={{ color: COLORS.link, fontWeight: 600 }}
            >
              {gradeName(g.grade, g.grade_label)}
            </Link>
            <Typography component="span" sx={SUB_SX}>
              {sampleCaption(g, unit)}
              {(g.small || g.count < threshold) && <SmallGroupMarker threshold={threshold} />}
            </Typography>
          </Box>
          <Box sx={CELL_SX}>{levelCell(g.level, g.level_scale, unit)}</Box>
          <Box sx={{ ...CELL_SX, color: resetYear ? COLORS.muted : COLORS.ink }}>
            {growthCell(g.growth, g.growth_scale, unit, resetYear, scoredSample(g, unit))}
          </Box>
          <Box sx={{ ...CELL_SX, width: 170, color: COLORS.muted }}>
            {growthCell(g.last_growth, g.last_growth_scale, unit, resetYear, 0)}
          </Box>
          <Box sx={CELL_SX}>
            <CountLink count={g.met} listKey={`${g.key}:met`} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function schoolsOmittedNote(omitted: number | undefined, shown: number, noun: string): string | undefined {
  if (!omitted || omitted <= 0) return undefined;
  return `${omitted} more school${omitted === 1 ? '' : 's'} not shown: this breakdown keeps the ${shown} with the most ${noun}.`;
}
